import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  Input,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  ControlContainer,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { DATEPICKER_DATE_FORMAT } from '@app/app.constants';
import { ServiceDataFields } from '@app/modules/service-data/components/service-data/service-data-form/field-sets/service-data-fields.enum';
import { ParticipantRelationship } from '@app/modules/service-data/types/paternity.types';
import {
  Ethnicity,
  Gender,
} from '@app/modules/service-data/types/service-data.types';
import { AccountUtilsService } from '@core/services/account-utils.service';
import { isBMIOver40 } from '@core/utils/core.util';
import { TranslatePipe } from '@ngx-translate/core';
import { SelectOptionComponent } from '@shared/components/select/select-option/select-option.component';
import { SelectComponent } from '@shared/components/select/select.component';
import { ValidationErrorComponent } from '@shared/components/validation-error/validation-error.component';
import { InputValidationStatusDirective } from '@shared/directives/input-validation-status.directive';
import { RequirePermissionDirective } from '@shared/directives/require-permission.directive';
import { controlContainerProvider } from '@shared/helpers';
import { combineLatest, map, startWith, Subject } from 'rxjs';

@Component({
  selector: 'app-service-data-form-additional-patient-information',
  imports: [
    InputValidationStatusDirective,
    ReactiveFormsModule,
    RequirePermissionDirective,
    TranslatePipe,
    ValidationErrorComponent,
    SelectComponent,
    SelectOptionComponent,
    AsyncPipe,
  ],
  templateUrl:
    './service-data-form-additional-patient-information.component.html',
  styleUrl: './service-data-form-additional-patient-information.component.scss',
  viewProviders: [controlContainerProvider],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ServiceDataFormAdditionalPatientInformationComponent
  implements OnInit
{
  private controlContainer = inject(ControlContainer);
  private authService = inject(AccountUtilsService);

  @Input({ required: true }) fieldSet: string[] = [];
  @Input() id: string;
  protected readonly ServiceDataFields = ServiceDataFields;
  protected readonly Gender = Gender;
  protected readonly Object = Object;
  protected readonly Ethnicity = Ethnicity;
  protected readonly relationships = Object.values(ParticipantRelationship);
  protected readonly addressFields = ['street', 'city', 'zip', 'country'];
  protected readonly DATEPICKER_DATE_FORMAT = DATEPICKER_DATE_FORMAT;

  protected highBMI$ = new Subject<boolean>();
  protected canViewAdditionalPatientInformationSection$ = combineLatest([
    this.authService.hasRequiredPermission$('medicalInformation'),
    this.authService.hasRequiredPermission$('serviceData.documentNumber'),
  ]).pipe(
    map(
      ([hasMedicalInformation, hasDocumentNumber]) =>
        !this.id || hasMedicalInformation || hasDocumentNumber
    )
  );

  private destroyRef = inject(DestroyRef);
  ethnicityFormControl: AbstractControl<any>;
  showEthnicityInput = signal(false);

  protected get form() {
    return this.controlContainer.control as FormGroup;
  }
  ngOnInit(): void {
    const heightControl = this.form.get('shared.patientInformation.height');
    const weightControl = this.form.get('shared.patientInformation.weight');

    combineLatest([
      heightControl.valueChanges.pipe(startWith(heightControl.value)),
      weightControl.valueChanges.pipe(startWith(weightControl.value)),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([height, weight]) =>
        this.highBMI$.next(this.calculateBMI(height, weight))
      );

    this.ethnicityFormControl = this.form.get(
      'shared.patientInformation.ethnicity'
    );

    const ethnicitySelectFormControl = this.form.get(
      'shared.patientInformation.ethnicitySelect'
    );

    ethnicitySelectFormControl.valueChanges
      .pipe(
        startWith(ethnicitySelectFormControl.value),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((ethnicity) => {
        if (ethnicity === Ethnicity.Other) {
          if (
            Object.values(Ethnicity).includes(this.ethnicityFormControl.value)
          ) {
            this.ethnicityFormControl.setValue(null);
          }
          this.showEthnicityInput.set(true);
        } else {
          this.ethnicityFormControl.setValue(ethnicity);
          this.showEthnicityInput.set(false);
        }
      });
  }

  private calculateBMI(height: number, weight: number) {
    if (!height || !weight) {
      return;
    }

    return isBMIOver40(weight, height);
  }
}
